/**
 * <hero-carousel>: Swiper with the Krista G home-slider options — centred
 * auto-width slides, endless loop both ways, grab-drag, autoplay that keeps
 * running after the shopper interacts.
 */
if (!customElements.get('hero-carousel')) {
  customElements.define(
    'hero-carousel',
    class HeroCarousel extends HTMLElement {
      connectedCallback() {
        if (this.swiper || typeof window.Swiper === 'undefined') return;

        const wrapper = this.querySelector('.swiper-wrapper');
        const slides = [...wrapper.children];
        this.count = slides.length;
        if (this.count < 2) return;

        for (const slide of slides) {
          slide.classList.add('swiper-slide');
          // Horizon's slideshow hooks; Swiper owns navigation here.
          slide.removeAttribute('on:click');
          slide.removeAttribute('aria-hidden');
        }

        // A centred loop needs spare slides on both sides of the active one,
        // or the far side shows a gap. Repeat the set until there are enough.
        const minimum = 6;
        for (let i = 0; wrapper.children.length < minimum || wrapper.children.length % this.count; i++) {
          const clone = slides[i % this.count].cloneNode(true);
          clone.removeAttribute('data-shopify-editor-block');
          clone.setAttribute('data-clone', '');
          wrapper.append(clone);
        }

        const delay = Number(this.dataset.autoplay);
        this.dots = [...this.querySelectorAll('.hero-carousel__dots button')];

        this.swiper = new window.Swiper(this, {
          slidesPerView: 'auto',
          centeredSlides: true,
          loop: true,
          spaceBetween: Number(this.dataset.gap) || 0,
          speed: 300,
          grabCursor: true,
          threshold: 2,
          slideToClickedSlide: true,
          keyboard: { enabled: true, onlyInViewport: true },
          autoplay: delay > 0 ? { delay, disableOnInteraction: false } : false,
          on: { realIndexChange: () => this.#syncDots() },
        });

        this.dots.forEach((dot, index) => dot.addEventListener('click', () => this.#goTo(index)));
        this.#syncDots();

        // Theme editor: jump to the slide being edited, and hold autoplay there.
        if (window.Shopify?.designMode) {
          document.addEventListener('shopify:block:select', this.#onBlockSelect);
          document.addEventListener('shopify:block:deselect', this.#onBlockDeselect);
        }
      }

      disconnectedCallback() {
        this.swiper?.destroy(true, true);
        this.swiper = null;
        document.removeEventListener('shopify:block:select', this.#onBlockSelect);
        document.removeEventListener('shopify:block:deselect', this.#onBlockDeselect);
      }

      #syncDots() {
        const active = this.swiper.realIndex % this.count;
        this.dots.forEach((dot, index) => dot.setAttribute('aria-selected', String(index === active)));
      }

      /** Go to the nearest copy of slide `index`, in whichever direction is shorter. */
      #goTo(index) {
        const total = this.swiper.slides.length;
        const current = this.swiper.realIndex;
        let target = index;
        let best = Infinity;
        for (let copy = index; copy < total; copy += this.count) {
          const forward = (copy - current + total) % total;
          const distance = Math.min(forward, total - forward);
          if (distance < best) [best, target] = [distance, copy];
        }
        this.swiper.slideToLoop(target);
      }

      #onBlockSelect = (event) => {
        if (!this.contains(event.target)) return;
        // Swiper's loop reorders the DOM; its slide-index attribute keeps the original order.
        const index = Number(event.target.closest('.swiper-slide')?.dataset.swiperSlideIndex);
        if (Number.isNaN(index)) return;
        this.swiper.autoplay?.stop();
        this.#goTo(index);
      };

      #onBlockDeselect = (event) => {
        if (this.contains(event.target) && Number(this.dataset.autoplay) > 0) this.swiper.autoplay?.start();
      };
    }
  );
}
